import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Store as StoreIcon,
  Check as CheckIcon,
  Toll as CreditIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useThemes, useCredits } from '../hooks';
import { ThemeCard, ThemePreview, ThemeStoreEducationModal, hasSeenThemeStoreEducation } from '../components/theme';
import { CreditBalance, CreditsPurchaseModal } from '../components';
import type { ThemeCategory, ThemeWithOwnership } from '../types';
import { THEME_CATEGORIES } from '../types';
import { useDocumentTitle } from '../hooks/core';
import { themesApi } from '../api';
import {
  trackThemeViewItemList,
  trackThemeViewItem,
  trackThemeAddToCart,
  trackThemeBeginCheckout,
  trackThemePurchase,
  type ThemeItem,
} from '../lib/analytics';

// Valid category values for URL routing
const VALID_CATEGORIES = THEME_CATEGORIES.map(c => c.value);

export const ThemeStorePage: React.FC = () => {
  const { category } = useParams<{ category?: string }>();
  const navigate = useNavigate();

  // Validate and normalize category from URL
  const activeCategory: ThemeCategory | 'all' = useMemo(() => {
    if (!category) return 'all';
    if (VALID_CATEGORIES.includes(category as ThemeCategory)) {
      return category as ThemeCategory;
    }
    return 'all';
  }, [category]);

  // Get category label for document title
  const categoryLabel = useMemo(() => {
    if (activeCategory === 'all') return 'All Themes';
    const cat = THEME_CATEGORIES.find(c => c.value === activeCategory);
    return cat ? `${cat.label} Themes` : 'Theme Store';
  }, [activeCategory]);

  useDocumentTitle(`Theme Store - ${categoryLabel}`);

  const { themes, isLoading, error, refresh } = useThemes();
  const { credits, refreshCredits } = useCredits();
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<ThemeWithOwnership | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [creditsPurchaseOpen, setCreditsPurchaseOpen] = useState(false);
  const [educationModalOpen, setEducationModalOpen] = useState(() => !hasSeenThemeStoreEducation());

  // Handle tab change - navigate to category URL
  const handleTabChange = (_: React.SyntheticEvent, value: ThemeCategory | 'all') => {
    if (value === 'all') {
      navigate('/themes');
    } else {
      navigate(`/themes/${value}`);
    }
  };

  // Filter themes by category
  const filteredThemes = useMemo(() => {
    if (activeCategory === 'all') {
      return themes.filter(t => t.active);
    }
    return themes.filter(t => t.category === activeCategory && t.active);
  }, [themes, activeCategory]);

  // Group themes by category for display, sorted alphabetically by name
  const freeThemes = useMemo(() =>
    filteredThemes.filter(t => t.isFree).sort((a, b) => a.name.localeCompare(b.name)),
    [filteredThemes]
  );
  const premiumThemes = useMemo(() =>
    filteredThemes.filter(t => !t.isFree).sort((a, b) => a.name.localeCompare(b.name)),
    [filteredThemes]
  );

  // Determine if we're viewing a premium-only category (NFL, NBA)
  const isPremiumCategory = activeCategory === 'nfl' || activeCategory === 'nba';

  // Helper to convert theme to analytics format
  const toThemeItem = useCallback((theme: ThemeWithOwnership): ThemeItem => ({
    id: theme.id,
    name: theme.name,
    category: theme.category,
    creditPrice: theme.creditPrice || 1,
  }), []);

  // Track ref to prevent duplicate view_item_list events
  const hasTrackedViewList = useRef(false);

  // Track theme list view when themes load
  useEffect(() => {
    if (!isLoading && filteredThemes.length > 0 && !hasTrackedViewList.current) {
      const analyticsItems = filteredThemes.map(toThemeItem);
      trackThemeViewItemList(analyticsItems);
      hasTrackedViewList.current = true;
    }
  }, [isLoading, filteredThemes, toThemeItem]);

  // Reset tracking when category changes
  useEffect(() => {
    hasTrackedViewList.current = false;
  }, [activeCategory]);

  const handlePurchaseClick = (theme: ThemeWithOwnership) => {
    // Track view_item and add_to_cart when user clicks purchase button
    const analyticsItem = toThemeItem(theme);
    trackThemeViewItem(analyticsItem);
    trackThemeAddToCart(analyticsItem);
    trackThemeBeginCheckout(analyticsItem);

    setSelectedTheme(theme);
    setPurchaseDialogOpen(true);
    setPurchaseError(null);
  };

  const handlePurchaseClose = () => {
    setPurchaseDialogOpen(false);
    setSelectedTheme(null);
    setPurchaseError(null);
  };

  const handlePurchaseConfirm = async () => {
    if (!selectedTheme) return;

    // Capture theme for analytics before state changes
    const purchasedTheme = toThemeItem(selectedTheme);

    setPurchasing(true);
    setPurchaseError(null);

    try {
      const result = await themesApi.purchaseTheme(selectedTheme.id);

      // Track successful purchase with transaction ID (using timestamp as fallback)
      const transactionId = `theme_${selectedTheme.id}_${Date.now()}`;
      trackThemePurchase(purchasedTheme, transactionId);

      handlePurchaseClose();
      setSuccessMessage(`Successfully purchased "${result.theme.name}" for ${result.creditsSpent} credit${result.creditsSpent !== 1 ? 's' : ''}!`);
      // Refresh themes to update ownership status
      refresh();
      // Refresh credits to show updated balance
      refreshCredits();
    } catch (err: unknown) {
      const error = err as { message?: string; status?: number };
      if (error.status === 402) {
        setPurchaseError('Insufficient credits. Please purchase more credits to buy this theme.');
      } else if (error.status === 409) {
        setPurchaseError('You already own this theme.');
      } else {
        setPurchaseError(error.message || 'Failed to purchase theme. Please try again.');
      }
    } finally {
      setPurchasing(false);
    }
  };

  const availableCredits = credits?.availableCredits ?? 0;
  const canAfford = selectedTheme ? availableCredits >= (selectedTheme.creditPrice || 1) : false;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StoreIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" component="h1">
                Theme Store
              </Typography>
              <Tooltip title="Learn more about themes">
                <IconButton
                  component={RouterLink}
                  to="/support/themes"
                  size="small"
                  sx={{ color: 'text.secondary' }}
                >
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body1" color="text.secondary">
              Browse and purchase themes to customize your boards
            </Typography>
          </Box>
        </Box>
        <CreditBalance variant="chip" />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button size="small" onClick={refresh} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Category Tabs - only show categories with active themes */}
      <Tabs
        value={activeCategory}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}
      >
        <Tab value="all" label="All Themes" />
        {THEME_CATEGORIES.map(cat => {
          const count = themes.filter(t => t.category === cat.value && t.active).length;
          // Only show categories that have active themes
          if (count === 0) return null;
          return (
            <Tab key={cat.value} value={cat.value} label={cat.label} />
          );
        })}
      </Tabs>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredThemes.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">
            No themes available in this category yet. Check back soon!
          </Typography>
        </Box>
      ) : (
        <>
          {/* Free Themes Section */}
          {freeThemes.length > 0 && (
            <Box sx={{ mb: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <CheckIcon color="success" />
                <Typography variant="h5">Free Themes</Typography>
              </Box>
              <Grid container spacing={3}>
                {freeThemes.map(theme => (
                  <Grid key={theme.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <ThemeCard theme={theme} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Premium Themes Section */}
          {premiumThemes.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <CreditIcon color="primary" />
                <Typography variant="h5">
                  {freeThemes.length > 0 || isPremiumCategory ? 'Premium Themes' : 'Themes'}
                </Typography>
              </Box>
              <Grid container spacing={3}>
                {premiumThemes.map(theme => (
                  <Grid key={theme.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <ThemeCard
                      theme={theme}
                      onPurchase={handlePurchaseClick}
                      isPurchasing={purchasing && selectedTheme?.id === theme.id}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </>
      )}

      {/* Purchase Dialog */}
      <Dialog open={purchaseDialogOpen} onClose={handlePurchaseClose} maxWidth="sm" fullWidth>
        <DialogTitle>Purchase Theme</DialogTitle>
        <DialogContent>
          {selectedTheme && (
            <Box>
              {/* Theme Preview */}
              <Box sx={{ mb: 3 }}>
                <ThemePreview theme={selectedTheme} size="large" showOwnershipBadge={false} hideInfo />
              </Box>

              <Typography variant="h6" gutterBottom>
                {selectedTheme.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {selectedTheme.description || 'A beautiful theme for your boards.'}
              </Typography>

              {/* Price Info */}
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Theme price:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {selectedTheme.creditPrice || 1} credit{(selectedTheme.creditPrice || 1) !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Your balance:</Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={canAfford ? 'success.main' : 'error.main'}
                  >
                    {availableCredits} credit{availableCredits !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Box>

              {purchaseError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {purchaseError}
                </Alert>
              )}

              {!canAfford && !purchaseError && (
                <Alert
                  severity="warning"
                  sx={{ mt: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setCreditsPurchaseOpen(true)}
                    >
                      Buy Credits
                    </Button>
                  }
                >
                  You need {(selectedTheme?.creditPrice || 1) - availableCredits} more credit{((selectedTheme?.creditPrice || 1) - availableCredits) !== 1 ? 's' : ''} to purchase this theme.
                </Alert>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                Once purchased, you can use this theme on unlimited boards. Theme ownership never expires.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePurchaseClose} disabled={purchasing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePurchaseConfirm}
            disabled={purchasing || !canAfford}
            startIcon={purchasing ? <CircularProgress size={16} /> : <CreditIcon />}
          >
            {purchasing ? 'Purchasing...' : `Purchase for ${selectedTheme?.creditPrice || 1} Credit${(selectedTheme?.creditPrice || 1) !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />

      {/* In-context Credits Purchase Modal */}
      <CreditsPurchaseModal
        open={creditsPurchaseOpen}
        onClose={() => setCreditsPurchaseOpen(false)}
        onSuccess={() => {
          setCreditsPurchaseOpen(false);
          refreshCredits();
        }}
        requiredCredits={selectedTheme?.creditPrice ?? 1}
        contextMessage={selectedTheme ? `Purchasing the "${selectedTheme.name}" theme requires ${selectedTheme.creditPrice ?? 1} credit${(selectedTheme.creditPrice ?? 1) !== 1 ? 's' : ''}.` : undefined}
      />

      {/* Theme Store Education Modal - shows on first visit */}
      <ThemeStoreEducationModal
        open={educationModalOpen}
        onClose={() => setEducationModalOpen(false)}
      />
    </Box>
  );
};

export default ThemeStorePage;
